import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import type { AppOptions, DealInput, DealRecord } from "../types";

function localDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function blankDeal(options: AppOptions): DealInput {
  return {
    date: localDate(),
    timeSlotId: options.timeSlots[0]?.id ?? "09:00",
    department: options.departments[0]?.id ?? "ford",
    customer: "",
    vehicle: "",
    stockNumber: "",
    salespersonId: options.salespeople[0]?.id ?? "",
    fiManagerId: options.fiManagers[0]?.id ?? "",
    typeNotes: ""
  };
}

function fromExisting(existing: DealRecord): DealInput {
  return {
    date: existing.date,
    timeSlotId: existing.timeSlotId,
    department: existing.department,
    customer: existing.customer,
    vehicle: existing.vehicle,
    stockNumber: existing.stockNumber,
    salespersonId: existing.salespersonId,
    fiManagerId: existing.fiManagerId,
    typeNotes: existing.typeNotes
  };
}

export default function DealForm({
  options,
  existing,
  onSaved,
  onCancel
}: {
  options: AppOptions;
  existing?: DealRecord;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const [deal, setDeal] = useState<DealInput>(() => existing ? fromExisting(existing) : blankDeal(options));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    setDeal(existing ? fromExisting(existing) : blankDeal(options));
    setReason("");
    setRequestId(crypto.randomUUID());
  }, [existing, options]);

  function set<K extends keyof DealInput>(key: K, value: DealInput[K]) {
    setDeal((current) => ({...current, [key]: value}));
    setMessage("");
    setError("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage(existing ? "Saving correction…" : "Saving deal…");

    try {
      if (existing) {
        if (!reason.trim()) throw new Error("Please enter a correction reason.");
        await api.updateDeal(requestId, existing, deal, reason.trim());
      } else {
        await api.createDeal(requestId, deal);
      }

      setMessage(existing ? "Deal corrected successfully." : "Deal entered successfully.");
      setRequestId(crypto.randomUUID());
      if (!existing) setDeal(blankDeal(options));
      onSaved();
    } catch (caught) {
      setMessage("");
      setError(caught instanceof Error ? caught.message : "The deal could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">PostgreSQL transaction</span>
          <h2>{existing ? "Edit Deal" : "Enter New Deal"}</h2>
        </div>
      </div>

      <form className="deal-form" onSubmit={submit}>
        <label>Date<input type="date" value={deal.date} onChange={(e) => set("date", e.target.value)} required /></label>
        <label>Time<select value={deal.timeSlotId} onChange={(e) => set("timeSlotId", e.target.value)}>{options.timeSlots.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
        <label>Department<select value={deal.department} onChange={(e) => set("department", e.target.value as DealInput["department"])}>{options.departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>Customer<input value={deal.customer} onChange={(e) => set("customer", e.target.value)} required maxLength={160} /></label>
        <label>Vehicle<input value={deal.vehicle} onChange={(e) => set("vehicle", e.target.value)} required maxLength={160} /></label>
        <label>Stock Number<input value={deal.stockNumber} onChange={(e) => set("stockNumber", e.target.value)} required maxLength={80} /></label>
        <label>Salesperson<select value={deal.salespersonId} onChange={(e) => set("salespersonId", e.target.value)} required>{options.salespeople.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>F&amp;I Manager<select value={deal.fiManagerId} onChange={(e) => set("fiManagerId", e.target.value)} required>{options.fiManagers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>Type / Deal<input list="type-notes" value={deal.typeNotes} onChange={(e) => set("typeNotes", e.target.value)} maxLength={160} placeholder="Type or choose: Retail, Fleet, FCTP, Offsite…" /><small className="field-hint">You can type anything or select a suggestion.</small><datalist id="type-notes">{options.typeNotes.map((item) => <option key={item} value={item} />)}</datalist></label>
        {existing && <label className="full-width">Correction reason<textarea value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={500} /></label>}

        <div className="form-actions full-width">
          <button className="primary" disabled={saving}>{saving ? "Saving…" : existing ? "Save Correction" : "Enter Deal"}</button>
          {onCancel && <button type="button" className="secondary" onClick={onCancel}>Cancel</button>}
        </div>

        {message && <p className="success full-width">{message}</p>}
        {error && <p className="error full-width">{error}</p>}
      </form>
    </section>
  );
}
